// EmailCaptureSystem — DOM modal that collects an email and POSTs it to
// the unified Supabase `waitlist` table. Triggered when a visitor picks
// the "I want to learn how to build something like this" dialogue branch.
//
// The `world: 'ian-world'` discriminator scopes rows to this app so the
// waitlist table can be shared across pixel-worlds. Schema + migration
// playbook in SUPABASE-SETUP.md.
//
// Supabase env vars (set in .env.local AND Vercel project env):
//   VITE_SUPABASE_URL       — e.g. https://abc123.supabase.co
//   VITE_SUPABASE_PUBLISHABLE_KEY  — publishable key from project settings

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const WORLD = 'ian-world';

export class EmailCaptureSystem {
  constructor() {
    this.isOpen = false;
    this.onClose = null;
    this._buildEl();
  }

  _buildEl() {
    const existing = document.querySelector('.iw-email-capture');
    if (existing) {
      this.root = existing;
      return;
    }

    const root = document.createElement('div');
    root.className = 'iw-email-capture';
    root.style.cssText = `
      position: fixed;
      inset: 0;
      display: none;
      align-items: center;
      justify-content: center;
      background: rgba(8, 6, 5, 0.65);
      z-index: 90;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      backdrop-filter: blur(2px);
    `;

    const card = document.createElement('div');
    card.style.cssText = `
      position: relative;
      max-width: 420px;
      width: calc(100% - 48px);
      padding: 28px 28px 24px;
      background: #1a1614;
      color: #f4ead5;
      border: 1px solid rgba(244, 234, 213, 0.18);
      border-radius: 12px;
      box-shadow: 0 14px 40px rgba(0, 0, 0, 0.5);
    `;

    // Close X
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = `
      position: absolute;
      top: 10px;
      right: 12px;
      background: transparent;
      border: none;
      color: #f4ead5;
      font-size: 22px;
      line-height: 1;
      cursor: pointer;
      padding: 4px 8px;
      opacity: 0.6;
      font-family: inherit;
      transition: opacity 140ms ease;
    `;
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.opacity = '1'; });
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.opacity = '0.6'; });
    closeBtn.addEventListener('click', () => this.close());

    const title = document.createElement('div');
    title.textContent = 'Want to know when it lands?';
    title.style.cssText = `
      font-size: 16px;
      font-weight: 700;
      letter-spacing: 0.02em;
      margin-bottom: 8px;
    `;

    const subtitle = document.createElement('div');
    subtitle.textContent = "Drop your email and I'll send you a note when the build-your-own-pixel-house thing is ready.";
    subtitle.style.cssText = `
      font-size: 12px;
      line-height: 1.5;
      color: rgba(244, 234, 213, 0.72);
      margin-bottom: 18px;
    `;

    const form = document.createElement('form');
    form.style.cssText = `display: flex; flex-direction: column; gap: 10px;`;

    const input = document.createElement('input');
    input.type = 'email';
    input.required = true;
    input.placeholder = 'you@somewhere.com';
    input.style.cssText = `
      padding: 10px 12px;
      background: #0e0c0a;
      border: 1px solid rgba(244, 234, 213, 0.2);
      border-radius: 6px;
      color: #f4ead5;
      font-family: inherit;
      font-size: 13px;
      outline: none;
    `;
    input.addEventListener('focus', () => { input.style.borderColor = '#fbbf24'; });
    input.addEventListener('blur',  () => { input.style.borderColor = 'rgba(244, 234, 213, 0.2)'; });

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.textContent = 'Let me know';
    submit.style.cssText = `
      padding: 10px 14px;
      background: #fbbf24;
      color: #1a1614;
      border: none;
      border-radius: 6px;
      font-family: inherit;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.04em;
      cursor: pointer;
      transition: opacity 140ms ease;
    `;
    submit.addEventListener('mouseenter', () => { submit.style.opacity = '0.85'; });
    submit.addEventListener('mouseleave', () => { submit.style.opacity = '1'; });

    const status = document.createElement('div');
    status.style.cssText = `
      font-size: 11px;
      color: rgba(244, 234, 213, 0.6);
      min-height: 16px;
      margin-top: 2px;
    `;

    form.appendChild(input);
    form.appendChild(submit);
    form.appendChild(status);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const email = input.value.trim();
      if (!email) return;
      submit.disabled = true;
      submit.style.opacity = '0.5';
      submit.style.cursor = 'wait';
      status.style.color = 'rgba(244, 234, 213, 0.6)';
      status.textContent = 'Sending…';
      try {
        await this._submit(email);
        status.style.color = '#7CC576';
        status.textContent = "Got it. I'll be in touch.";
        input.disabled = true;
        submit.textContent = 'Sent';
        setTimeout(() => this.close(), 2200);
      } catch (err) {
        status.style.color = '#E6635A';
        status.textContent = err.message || "Something went wrong. Try again?";
        submit.disabled = false;
        submit.style.opacity = '1';
        submit.style.cursor = 'pointer';
      }
    });

    card.appendChild(closeBtn);
    card.appendChild(title);
    card.appendChild(subtitle);
    card.appendChild(form);
    root.appendChild(card);
    document.body.appendChild(root);

    // Click-outside closes
    root.addEventListener('click', (event) => {
      if (event.target === root) this.close();
    });

    // ESC closes
    this._escHandler = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    };
    document.addEventListener('keydown', this._escHandler);

    this.root = root;
    this.input = input;
    this.status = status;
    this.submitBtn = submit;
  }

  async _submit(email) {
    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
      // Local dev without env vars — just log and pretend it worked. Lets
      // the UI flow be tested without hitting Supabase.
      console.warn('[EmailCapture] Supabase env vars not set — skipping insert', { email });
      return;
    }
    const res = await fetch(`${SUPABASE_URL}/rest/v1/waitlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        Prefer: 'return=minimal',
      },
      body: JSON.stringify({ world: WORLD, email }),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(body.includes('duplicate') ? "You're already on the list." : 'Could not save email.');
    }
  }

  open({ onClose } = {}) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.onClose = onClose ?? null;
    // Reset state in case modal was opened previously
    if (this.input) {
      this.input.value = '';
      this.input.disabled = false;
    }
    if (this.status) this.status.textContent = '';
    if (this.submitBtn) {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent = 'Let me know';
      this.submitBtn.style.opacity = '1';
      this.submitBtn.style.cursor = 'pointer';
    }
    this.root.style.display = 'flex';
    setTimeout(() => this.input?.focus(), 50);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.style.display = 'none';
    this.onClose?.();
  }
}
